/**
 * localization-prompt — offers the storefront language that matches where the
 * visitor actually is, and lets them decline.
 *
 * Why this exists at all, given Shopify ships "automatic redirection":
 *
 *   Shopify's COUNTRY redirection only fires when the resolved market has its own
 *   domain, subdomain or subfolder to redirect TO. This store has exactly one web
 *   presence (pt-BR at `/`, en at `/en`) shared by every country, so country
 *   redirection resolves AE, BR and US to the same URL and does nothing.
 *
 *   Shopify's LANGUAGE redirection does work, but it reads Accept-Language, not the
 *   IP. A Dubai shopper whose phone is set to Arabic matches no published language
 *   and falls through to the web presence default — Portuguese.
 *
 * So the country signal has to come from `/browsing_context_suggestions.json`, which
 * is Shopify's own GeoIP endpoint (the one that used to back the now-removed
 * Geolocation app) and needs no third-party service, no API key and no extra consent
 * banner. `detected_values.country.handle` is the visitor's resolved ISO country.
 *
 * We deliberately do NOT read `suggestions[]` from that response. Verified against
 * two live stores from the same IP: one returned a populated `suggestions` array and
 * the other returned `"suggestions": []` while still resolving the country correctly.
 * Shopify only populates it when ITS heuristics think there is a better context, and
 * those heuristics are language-first. The country→language policy is a merchant
 * decision, so it lives in the section settings and is applied here.
 *
 * Resolution order, matching that policy:
 *   1. The visitor's country has an explicit rule  → use it. (BR → pt-BR)
 *   2. Otherwise their browser asks for a language we publish → use that, so a
 *      Brazilian in Dubai is not pushed into English.
 *   3. Otherwise → the fallback language. (AE, and the rest of the world → en)
 *
 * The result is a suggestion, never a wall. Shopify's own guidance is not to trap
 * visitors in their detected context — VPNs, corporate egress and travellers all
 * make GeoIP wrong some of the time, and someone browsing deliberately in their
 * second language has already told you more than an IP can.
 */

(() => {
  const STORAGE_KEY = 'ghalya_localization_prompt';
  const STORAGE_VERSION = 1;
  const REQUEST_TIMEOUT_MS = 4000;
  const COUNTRY_PLACEHOLDER = '__country__';

  class LocalizationPrompt extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();
      if (!this.config) return;

      this.storage = this.resolveStorage();
      this.panel = this.querySelector('[data-panel]');
      this.lede = this.querySelector('[data-lede]');

      this.querySelectorAll('[data-dismiss]').forEach((button) =>
        button.addEventListener('click', () => this.decline())
      );
      this.querySelectorAll('[data-switch-form]').forEach((option) =>
        option.addEventListener('submit', () => this.remember(option.dataset.switchForm))
      );

      // The merchant has to be able to see and style it while it is selected in the
      // theme editor, where there is no meaningful visitor to detect. Any language other
      // than the one being previewed will do — the fallback is not necessarily one of
      // them, since the editor may already be open on the fallback's own storefront.
      if (window.Shopify && window.Shopify.designMode) {
        const preview = this.config.available.find((locale) => locale !== this.config.current);
        if (preview) this.open(preview);
        return;
      }

      this.start().catch(() => {});
    }

    readConfig() {
      try {
        return JSON.parse(this.querySelector('[data-config]').textContent);
      } catch (_) {
        return null;
      }
    }

    async start() {
      if (this.decided()) return;

      // Auto mode navigates on the visitor's behalf, and the only thing stopping that
      // from repeating on the destination page is the remembered decision. With no
      // writable storage that becomes a redirect loop, so degrade to asking.
      const canRedirect = this.config.mode === 'auto' && this.storage !== null;

      const country = await this.detectCountry();
      const target = this.resolveLocale(country && country.handle);
      if (!target || target === this.config.current) return;

      if (canRedirect) {
        this.remember(target);
        this.submit(target);
        return;
      }

      this.open(target, country && country.name);
    }

    /**
     * The visitor's ISO country per Shopify's edge, or null.
     *
     * Every failure here is non-fatal by design: while the storefront is
     * password-protected this endpoint 302s to /password, which answers 200 with HTML,
     * so an `ok` response is not enough — the content type has to be checked too.
     */
    async detectCountry() {
      const url = `${this.config.root.replace(/\/$/, '')}/browsing_context_suggestions.json?${new URLSearchParams(
        { 'country[enabled]': 'true' }
      )}`;

      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(url, {
          signal: abort.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;
        if (!(response.headers.get('content-type') || '').includes('application/json')) return null;
        const payload = await response.json();
        return (payload && payload.detected_values && payload.detected_values.country) || null;
      } catch (_) {
        return null;
      } finally {
        clearTimeout(timer);
      }
    }

    resolveLocale(countryCode) {
      const ruled = countryCode && this.config.rules[countryCode.toUpperCase()];
      if (ruled && this.config.available.includes(ruled)) return ruled;

      if (this.config.preferBrowserLanguage) {
        const preferred = this.browserLocale();
        if (preferred) return preferred;
      }

      return this.config.available.includes(this.config.fallback) ? this.config.fallback : null;
    }

    /**
     * The first published locale the browser asks for. `navigator.languages` is ordered
     * by preference and carries region subtags the store may not publish, so `pt-PT`
     * has to be able to match the published `pt-BR` on its primary subtag alone.
     */
    browserLocale() {
      const requested = navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language].filter(Boolean);

      for (const tag of requested) {
        const wanted = tag.toLowerCase();
        const exact = this.config.available.find((locale) => locale.toLowerCase() === wanted);
        if (exact) return exact;

        const base = wanted.split('-')[0];
        const loose = this.config.available.find((locale) => locale.toLowerCase().split('-')[0] === base);
        if (loose) return loose;
      }
      return null;
    }

    open(locale, countryName) {
      const option = this.option(locale);
      if (!option) return;

      option.hidden = false;
      if (this.lede) {
        const template = countryName ? this.config.copy.title : this.config.copy.titleGeneric;
        this.lede.textContent = decodeEntities(template).replace(COUNTRY_PLACEHOLDER, countryName || '');
      }
      this.setAttribute('open', '');
    }

    decline() {
      this.remember(this.config.current);
      this.removeAttribute('open');
    }

    submit(locale) {
      const form = this.option(locale) && this.option(locale).querySelector('form');
      if (form) form.submit();
    }

    option(locale) {
      return this.querySelector(`[data-switch-form="${CSS.escape(locale)}"]`);
    }

    decided() {
      if (!this.storage) return false;
      try {
        const raw = this.storage.getItem(STORAGE_KEY);
        if (!raw) return false;
        return JSON.parse(raw).v === STORAGE_VERSION;
      } catch (_) {
        return false;
      }
    }

    remember(locale) {
      if (!this.storage) return;
      try {
        this.storage.setItem(STORAGE_KEY, JSON.stringify({ v: STORAGE_VERSION, locale }));
      } catch (_) {}
    }

    /** localStorage throws outright in some privacy modes, so probe before trusting it. */
    resolveStorage() {
      for (const candidate of [window.localStorage, window.sessionStorage]) {
        try {
          candidate.setItem(STORAGE_KEY + '_probe', '1');
          candidate.removeItem(STORAGE_KEY + '_probe');
          return candidate;
        } catch (_) {}
      }
      return null;
    }
  }

  /** Shopify's `t` filter HTML-escapes the copy (you're → you&#39;re), and textContent would print it literally. */
  function decodeEntities(text) {
    return new DOMParser().parseFromString(text, 'text/html').documentElement.textContent;
  }

  if (!customElements.get('localization-prompt')) {
    customElements.define('localization-prompt', LocalizationPrompt);
  }
})();
